import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, User, ChevronDown, Settings } from 'lucide-react';
import clsx from 'clsx';
import { isTenantAdminUser, useAuthStore } from '../../store/authStore';
import { useLogout } from '../../api/hooks/useLogout';
import { useMyTenants, useSwitchActiveTenant } from '../../api/hooks/useTenants';
import { usePlanFeatures } from '@api/hooks/useSubscription';

export const UserMenu = React.forwardRef<HTMLDivElement>((_props, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentUser, activeTenantId, hasPermission } = useAuthStore();
  const { mutate: logout } = useLogout();
  const { data: tenants } = useMyTenants();
  const switchTenant = useSwitchActiveTenant();

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  // Click-outside detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Keyboard support (ESC to close)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen]);

  const getInitials = (firstName?: string, lastName?: string, email?: string): string => {
    if (firstName) {
      return (firstName[0] || '') + (lastName?.[0] || '');
    }
    if (email) {
      return email[0]?.toUpperCase() || '';
    }
    return 'U';
  };

  const displayName = currentUser
    ? `${currentUser.first_name} ${currentUser.last_name}`.trim() || currentUser.email || 'User'
    : 'User';
  const initials = getInitials(currentUser?.first_name, currentUser?.last_name, currentUser?.email);
  const planFeatures = usePlanFeatures();
  const isVendorTier = planFeatures.vendor_profile === true;

  const canAccessAdminSettings = !isVendorTier && (isTenantAdminUser(currentUser) || [
    'user.read',
    'user.write',
    'role.read',
    'role.write',
    'org_unit.write',
    'approval_chain.write',
  ].some((permission) => hasPermission(permission)));

  return (
    <div ref={ref} className="relative">
      <div ref={dropdownRef} className="relative">
        {/* Trigger Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={clsx(
            'flex items-center gap-2 px-3 py-2 rounded-lg',
            'text-content-primary',
            'hover:bg-surface-raised',
            'transition-colors duration-150',
            'border border-transparent',
            isOpen && 'bg-surface-raised'
          )}
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          {/* User Avatar/Initials */}
          <div
            className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center',
              'bg-primary-500 text-white text-sm font-semibold'
            )}
          >
            {initials}
          </div>
          {/* User Name */}
          <span className="hidden sm:inline text-sm font-medium">{displayName}</span>
          <ChevronDown
            size={16}
            className={clsx('transition-transform duration-200', isOpen && 'rotate-180')}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div
            className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg bg-surface-base border border-border-default z-50 py-1"
            role="menu"
          >
            {/* User Header */}
            <div className="px-4 py-3 border-b border-border-subtle">
              <p className="text-sm font-semibold text-content-primary">
                {displayName}
              </p>
              {currentUser?.email && (
                <p className="text-xs text-content-muted mt-1">
                  {currentUser.email}
                </p>
              )}
              {currentUser?.active_tenant && (
                <p className="text-xs text-content-muted mt-1">
                  Tenant: {currentUser.active_tenant.name}
                </p>
              )}
            </div>

            {tenants && tenants.length > 1 && (
              <div className="px-4 py-3 border-b border-border-subtle space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-content-muted">
                  Switch Tenant
                </p>
                <div className="space-y-1">
                  {tenants.map((tenant) => {
                    const isActive = tenant.id === (activeTenantId ?? currentUser?.active_tenant_id);
                    return (
                      <button
                        key={tenant.id}
                        type="button"
                        disabled={isActive || switchTenant.isPending}
                        onClick={() => {
                          void switchTenant.mutateAsync(tenant.id);
                          setIsOpen(false);
                        }}
                        className={clsx(
                          'w-full text-left px-2 py-1.5 rounded text-sm transition-colors',
                          isActive
                            ? 'bg-surface-raised text-content-primary'
                            : 'text-content-secondary hover:bg-surface-raised hover:text-content-primary'
                        )}
                      >
                        {tenant.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Menu Items */}
            <div className="py-1">
              {/* View Profile */}
              <Link
                to="/profile"
                onClick={() => setIsOpen(false)}
                className={clsx(
                  'flex items-center gap-3 px-4 py-2',
                  'text-content-primary',
                  'hover:bg-surface-raised',
                  'transition-colors duration-150',
                  'text-sm'
                )}
                role="menuitem"
              >
                <User size={16} />
                View Profile
              </Link>

              {/* Admin Settings - Only visible to admins */}
              {canAccessAdminSettings && (
                <Link
                  to="/admin/settings"
                  onClick={() => setIsOpen(false)}
                  className={clsx(
                    'flex items-center gap-3 px-4 py-2',
                    'text-content-primary',
                    'hover:bg-surface-raised',
                    'transition-colors duration-150',
                    'text-sm'
                  )}
                  role="menuitem"
                >
                  <Settings size={16} />
                  Admin Settings
                </Link>
              )}

              {/* Divider */}
              <div className="my-1 border-t border-border-subtle" />

              {/* Logout */}
              <button
                onClick={handleLogout}
                className={clsx(
                  'w-full flex items-center gap-3 px-4 py-2',
                  'text-content-primary',
                  'hover:bg-red-500/10',
                  'hover:text-red-500',
                  'transition-colors duration-150',
                  'text-sm text-left'
                )}
                role="menuitem"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

UserMenu.displayName = 'UserMenu';
